import { defineStore } from "pinia"
import { getMeterpointsForPostcode } from "@/services-handlers/getMeterpointsForPostcode"
import { TariffService } from "@/services/TariffService"
import { MeterpointAddress } from "@/models/meterpointAddress/meterpointAddress"
import {
  IMeterpointAddress,
  IMPANAddress,
  IMPRNAddress,
  IMPXNAddress,
} from "@/models/meterpointAddress/meterpointAddress.types"
import { ITariffBundle } from "@/models/tariffBundle/tariffBundle.types"
import { TariffBundle } from "@/models/tariffBundle/tariffBundle"
import { ITariff } from "@/models/tariff/tariff.types"
import { IUsageEstimation } from "@/services/UsageEstimationService/types"
import { GSP_MAP } from "@/constants/meterpoints"
import {
  filterDuplicatesForProperty,
  filterDuplicatesByProperties,
  groupObjectsByProperty,
} from "@/helpers/arrayHelpers"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import { RemoveReadonly } from "@/helpers/typeHelper"
import { ConfigurationService } from "@/services/ConfigurationService"
import {
  ElectricityVatChange,
  NO_ELECTRICITY_VAT_CHANGE,
  isElectricityVatChangeActive,
  rebaseElectricityTariff,
} from "@/helpers/vatHelper"

type SearchTerm = "GSP" | "Postcode"
type MPXN = "MPAN" | "MPRN"

export type FuelType = "gas" | "electricity"
export interface IMPXNUsage {
  id: string
  type: MPXN
  usage: Partial<IUsageEstimation>
  isTwoRate?: boolean

  readonly isElectricity: boolean
}

export class MPXNUsage implements IMPXNUsage {
  id: string
  type: MPXN
  usage: Partial<IUsageEstimation>
  isTwoRate?: boolean

  constructor(
    id: string,
    type: MPXN,
    usage: Partial<IUsageEstimation>,
    isTwoRate?: boolean,
  ) {
    this.id = id
    this.type = type
    this.usage = usage
    this.isTwoRate = isTwoRate
  }

  get isElectricity() {
    return this.type === "MPAN"
  }
}

export interface ITariffQuoterStore {
  searchTerm: SearchTerm
  gsp: string | null
  postcode: string | null
  selectedMeterpointAddress: IMeterpointAddress | null
  selectedFuels: FuelType[]
  isManuallySelected: boolean
  fuelEstimation: Partial<IUsageEstimation>
  fuelUsage: Partial<IUsageEstimation>
  quoteForMeterpoints: boolean
  meterpointFuelUsage: IMPXNUsage[]
  isDirectDebit: boolean
  includeNSC: boolean
  showTariffBreakdown: boolean
  selectedBundle?: string
  selectedTariffsFuel: string[]
  selectedTariffsMeterpoint: string[]

  meterpointsForPostcode: AggregatedQueryProps<MeterpointAddress[]>
  isGSPSearch: boolean
  showResults: boolean
  gspName?: string
  mpans: IMPANAddress[]
  mprns: IMPRNAddress[]
  address: IMPXNAddress
  isElectricitySelected: boolean
  isGasSelected: boolean
  isTwoRateSelected: boolean
  hasExportMeter: boolean
  totalFuelEstimation: number
  combinedFuelUsage: IUsageEstimation
  combinedMeterpointFuelUsage: IUsageEstimation
  electricityVatChange: ElectricityVatChange
  rebasedTariffs: ITariff[]
  tariffBundlesForGsp: ITariffBundle[]
  tariffsForGsp: ITariff[]
  isLoading: boolean
  selectedUsage: IUsageEstimation
  filteredBundles: ITariffBundle[]
  showTariffSummary: boolean
  setSelectedMeterpoint: (val: IMeterpointAddress | null) => void
  setMeterpointFuelUsage: () => void
  setSelectedTariffs: (val?: string) => void
}

function getSelectedFuels(val: IMeterpointAddress): FuelType[] {
  if (val.mpans.length && val.mprns.length) {
    return ["electricity", "gas"]
  } else if (val.mpans.length) {
    return ["electricity"]
  } else if (val.mprns.length) {
    return ["gas"]
  } else {
    return []
  }
}

export const useTariffQuoter = defineStore("tariffQuoter", {
  state: () => {
    return {
      searchTerm: "Postcode",
      gsp: null,
      postcode: null,
      selectedMeterpointAddress: null,
      selectedFuels: [] as FuelType[],
      isManuallySelected: false,
      fuelEstimation: {} as Partial<IUsageEstimation>,
      fuelUsage: {} as Partial<IUsageEstimation>,
      quoteForMeterpoints: false,
      meterpointFuelUsage: [] as IMPXNUsage[],
      isDirectDebit: true,
      includeNSC: false,
      showTariffBreakdown: false,
      selectedBundle: undefined,
      selectedTariffsFuel: [] as string[],
      selectedTariffsMeterpoint: [] as string[],
    } as ITariffQuoterStore
  },
  getters: {
    meterpointsForPostcode() {
      return getMeterpointsForPostcode(this.postcode ?? "")
    },
    isGSPSearch() {
      return this.searchTerm === "GSP"
    },
    showResults() {
      return this.searchTerm === "GSP"
        ? !!this.gsp
        : !!this.selectedMeterpointAddress?.gsp
    },
    gspName() {
      return GSP_MAP.get(this.gsp ?? "")
    },
    mpans() {
      return this.selectedMeterpointAddress?.mpans ?? []
    },
    mprns() {
      return this.selectedMeterpointAddress?.mprns ?? []
    },
    address() {
      return this.selectedMeterpointAddress?.address
    },
    isElectricitySelected() {
      return this.selectedFuels.includes("electricity")
    },
    isGasSelected() {
      return this.selectedFuels.includes("gas")
    },
    isTwoRateSelected() {
      return (
        typeof this.fuelUsage.electricityDay === "number" ||
        typeof this.fuelUsage.electricityNight === "number"
      )
    },
    hasExportMeter() {
      return this.mpans.some((m) => m.isExport)
    },
    totalFuelEstimation() {
      const elec = this.isElectricitySelected
        ? (this.fuelEstimation.electricity ?? 0) +
          (this.fuelEstimation.electricityDay ?? 0) +
          (this.fuelEstimation.electricityNight ?? 0)
        : 0
      const gas = this.isGasSelected ? (this.fuelEstimation.gas ?? 0) : 0
      return elec + gas
    },
    combinedFuelUsage() {
      const gas = this.isGasSelected ? this.fuelUsage.gas : undefined
      const elec = this.isElectricitySelected
        ? this.fuelUsage.electricity
        : undefined
      const elecDay = this.isElectricitySelected
        ? this.fuelUsage.electricityDay
        : undefined
      const elecNight = this.isElectricitySelected
        ? this.fuelUsage.electricityNight
        : undefined
      return {
        gas: gas,
        electricity: elec,
        electricityDay: elecDay,
        electricityNight: elecNight,
      } as IUsageEstimation
    },
    combinedMeterpointFuelUsage() {
      const result = {
        gas: 0,
        electricity: 0,
        electricityDay: 0,
        electricityNight: 0,
      } as IUsageEstimation

      this.meterpointFuelUsage.forEach((mpxn) => {
        if (!mpxn.isElectricity) {
          result.gas += mpxn.usage.gas ?? 0
        } else if (mpxn.isElectricity && !mpxn.isTwoRate) {
          result.electricity += mpxn.usage.electricity ?? 0
        } else if (mpxn.isElectricity && mpxn.isTwoRate) {
          result.electricityDay += mpxn.usage.electricityDay ?? 0
          result.electricityNight += mpxn.usage.electricityNight ?? 0
        }
      })

      result.gas = this.isGasSelected ? result.gas : 0
      result.electricity = this.isElectricitySelected ? result.electricity : 0
      result.electricityDay = this.isElectricitySelected
        ? result.electricityDay
        : 0
      result.electricityNight = this.isElectricitySelected
        ? result.electricityNight
        : 0

      return result
    },
    electricityVatChange() {
      const from = ConfigurationService.getElectricityVatChangeFrom().data
      const newRate = ConfigurationService.getElectricityVatChangeNewRate().data
      if (newRate == null || !isElectricityVatChangeActive(from)) {
        return NO_ELECTRICITY_VAT_CHANGE
      }
      return { active: true, newRate }
    },
    rebasedTariffs() {
      const tariffs = TariffService.getForGsp(this.gsp ?? "").data ?? []
      const change = this.electricityVatChange
      return change.active
        ? tariffs.map((tariff) =>
            rebaseElectricityTariff(tariff, change.newRate),
          )
        : tariffs
    },
    tariffBundlesForGsp() {
      const bundles = groupObjectsByProperty(
        this.rebasedTariffs,
        "bundleCode",
      ) as ITariff[][]
      return filterDuplicatesByProperties(
        bundles.map((bundle) => new TariffBundle(bundle)),
        "name",
        "isNSC",
      )
    },
    tariffsForGsp() {
      return filterDuplicatesForProperty(this.rebasedTariffs, "code")
    },
    isLoading() {
      // The VAT config must resolve before prices render — otherwise the table
      // shows un-stripped 5% electricity prices and then silently re-renders.
      return (
        TariffService.getForGsp(this.gsp ?? "").isFetching ||
        ConfigurationService.getElectricityVatChangeFrom().isFetching ||
        ConfigurationService.getElectricityVatChangeNewRate().isFetching
      )
    },
    selectedUsage() {
      return this.quoteForMeterpoints
        ? this.combinedMeterpointFuelUsage
        : this.combinedFuelUsage
    },
    filteredBundles() {
      return this.tariffBundlesForGsp.filter((bundle) => {
        if (this.isDirectDebit && !bundle.hasDirectDebit) {
          return false
        }
        if (!this.includeNSC && bundle.isNSC) {
          return false
        }
        if (this.isGasSelected && !bundle.gas) {
          return false
        }
        if (this.isElectricitySelected) {
          if (!bundle.elec1Rate && !!this.selectedUsage.electricity) {
            return false
          }

          if (
            (!!this.selectedUsage.electricityDay ||
              !!this.selectedUsage.electricityNight) &&
            !(bundle.elec2Rate || bundle.elec1Rate)
          ) {
            return false
          }
        }
        return true
      })
    },
    showTariffSummary(state) {
      return (
        !!state.selectedBundle ||
        (this.quoteForMeterpoints
          ? state.selectedTariffsMeterpoint.every((m) => m !== "")
          : state.selectedTariffsFuel.every((f) => f !== ""))
      )
    },
  },
  actions: {
    setSelectedMeterpoint(val: IMeterpointAddress | null) {
      this.selectedMeterpointAddress = val
      this.selectedFuels = val ? getSelectedFuels(val) : []
    },
    setMeterpointFuelUsage() {
      const mpans = this.selectedMeterpointAddress?.mpans ?? []
      const mprns = this.selectedMeterpointAddress?.mprns ?? []

      const newEstimation = { ...this.fuelEstimation }

      const electUsage = newEstimation.electricity
        ? newEstimation.electricity
        : newEstimation.electricityDay || newEstimation.electricityNight
          ? (newEstimation.electricityDay ?? 0) +
            (newEstimation.electricityNight ?? 0)
          : 0

      const defaultElecUsageEstimation: Partial<IUsageEstimation> = {
        electricity: Math.floor(electUsage / mpans.length),
        electricityDay: Math.floor(electUsage / mpans.length / 2),
        electricityNight: Math.floor(electUsage / mpans.length / 2),
      }
      const defaultGasUsageEstimation: Partial<IUsageEstimation> = {
        gas: Math.floor((newEstimation.gas ?? 0) / mprns.length),
      }

      const mpanUsages = mpans
        .filter((m) => !m.isExport)
        .map(
          (mpan) =>
            new MPXNUsage(mpan.identifier, "MPAN", {
              ...defaultElecUsageEstimation,
            }),
        )
      const mprnUsages = mprns.map(
        (mprn) =>
          new MPXNUsage(mprn.identifier, "MPRN", {
            ...defaultGasUsageEstimation,
          }),
      )

      const selectedMpanUsages = this.isElectricitySelected ? mpanUsages : []
      const selectedMprnUsages = this.isGasSelected ? mprnUsages : []

      this.meterpointFuelUsage = selectedMpanUsages.concat(selectedMprnUsages)
    },
    setSelectedTariffs(val?: string) {
      this.selectedTariffsFuel = [
        this.isElectricitySelected ? (val ?? this.selectedBundle ?? "") : "",
        this.isGasSelected ? (val ?? this.selectedBundle ?? "") : "",
      ]
      this.selectedTariffsMeterpoint = Array.from(
        { length: this.meterpointFuelUsage.length },
        () => val ?? this.selectedBundle ?? "",
      )
    },
    getAnnualCostForBundle(bundle: ITariffBundle) {
      if (this.quoteForMeterpoints) {
        return this.meterpointFuelUsage.reduce((total, mpxn) => {
          return (
            total +
            bundle.annualCost({
              usage: mpxn.usage,
              isElectricity: mpxn.isElectricity,
              isTwoRate: mpxn.isTwoRate,
            })
          )
        }, 0)
      } else {
        return bundle.annualCost({ usage: this.selectedUsage })
      }
    },
    getAnnualCostForSelectedBundles() {
      const bundles = this.tariffBundlesForGsp
      if (this.quoteForMeterpoints) {
        const selected = this.selectedTariffsMeterpoint
        return this.meterpointFuelUsage.reduce((total, mpxn, index) => {
          const bundle = bundles.find(
            (bundle) => bundle.code === selected[index],
          )
          return (
            total +
            (bundle?.annualCost({
              usage: mpxn.usage,
              isElectricity: mpxn.isElectricity,
              isTwoRate: mpxn.isTwoRate,
            }) ?? 0)
          )
        }, 0)
      } else {
        const elecBundle = bundles.find(
          (bundle) => bundle.code === this.selectedTariffsFuel[0],
        )
        const gasBundle = bundles.find(
          (bundle) => bundle.code === this.selectedTariffsFuel[1],
        )

        const elecCost =
          elecBundle?.annualCost({
            usage: this.fuelUsage,
            isElectricity: true,
            isTwoRate: this.isTwoRateSelected,
          }) ?? 0

        const gasCost =
          gasBundle?.annualCost({
            usage: this.fuelUsage,
            isElectricity: false,
          }) ?? 0

        return elecCost + gasCost
      }
    },
  },
})

export type TariffQuoterStore = ReturnType<typeof useTariffQuoter>
export type TariffQuoterTestStore = RemoveReadonly<
  ReturnType<typeof useTariffQuoter>
>
